// Quick add: N from any screen (not while typing) opens a one-line task creator.
// Enter saves; Shift+Enter saves and opens the full panel. BlackWolf only.
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTaskActions, useTracker } from '@/lib/tracker';
import type { Program } from '@/lib/types';
import { Dialog } from '@/components/ui/dialog';
import { Field, Input, Select } from '@/components/ui/field';
import { Button } from '@/components/ui/button';
import { Kbd } from '@/components/ui/misc';

const Ctx = createContext<() => void>(() => {});
export const useQuickAdd = () => useContext(Ctx);

export const isTyping = (e: KeyboardEvent) => {
  const el = e.target as HTMLElement | null;
  return !!el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));
};

export function QuickAddProvider({ program, children }: { program: Program; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const enabled = program.capabilities.includes('plan.manage');

  useEffect(() => {
    if (!enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (
        (e.key === 'n' || e.key === 'N') &&
        !e.ctrlKey &&
        !e.metaKey &&
        !e.altKey &&
        !isTyping(e) &&
        !document.querySelector('dialog[open],[role=dialog]')
      ) {
        e.preventDefault();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [enabled]);

  return (
    <Ctx.Provider value={() => enabled && setOpen(true)}>
      {children}
      {enabled && <QuickAddDialog program={program} open={open} onClose={() => setOpen(false)} />}
    </Ctx.Provider>
  );
}

function QuickAddDialog({ program, open, onClose }: { program: Program; open: boolean; onClose: () => void }) {
  const tracker = useTracker(program.id);
  const { create } = useTaskActions(program.id);
  const [params, setParams] = useSearchParams();
  const [title, setTitle] = useState('');
  const [phaseId, setPhaseId] = useState('');
  const data = tracker.data;

  // Default to the phase being filtered, else the earliest phase in flight.
  useEffect(() => {
    if (!open || !data) return;
    const filtered = params.get('phase');
    const flying = data.phases.find((p) => p.forecastStart <= data.today && data.today <= p.forecastEnd);
    setPhaseId(filtered ?? flying?.id ?? data.phases[0]?.id ?? '');
    setTitle('');
  }, [open, data, params]);

  function submit(openPanel: boolean) {
    if (!title.trim()) return;
    create.mutate(
      { title: title.trim(), phaseId: phaseId || undefined },
      {
        onSuccess: (t) => {
          onClose();
          if (openPanel) {
            const next = new URLSearchParams(params);
            next.set('task', t.id);
            setParams(next);
          }
        },
      },
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="New task"
      footer={
        <>
          <span className="mr-auto self-center text-xs text-muted">
            <Kbd>Enter</Kbd> save · <Kbd>Shift Enter</Kbd> save and open
          </span>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={create.isPending} onClick={() => submit(false)}>
            Add task
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Title" htmlFor="qa-title">
          <Input
            id="qa-title"
            autoFocus
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit(e.shiftKey);
              }
            }}
          />
        </Field>
        <Field label="Phase" htmlFor="qa-phase">
          <Select id="qa-phase" value={phaseId} onChange={(e) => setPhaseId(e.target.value)}>
            {data?.phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.number}. {p.name}
              </option>
            ))}
          </Select>
        </Field>
      </div>
    </Dialog>
  );
}
